"use client";

import { signOutAction } from "@/app/actions";
import Link from "next/link";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User as UserIcon, LogOut, Loader2, LogIn } from "lucide-react";
import { createBrowserSupabaseClient } from "@/utils/supabase/client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/auth-context";
import { useTheme } from "next-themes";
import { usePathname } from 'next/navigation';

export const dynamic = "force-dynamic";


interface HeaderAuthProps {
  isMenuOpen: boolean
}

export const HeaderAuth: React.FC<HeaderAuthProps> = ({
  isMenuOpen
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const { isAuthenticated, userEmail, userAvatar } = useAuth();
  const [profileAvatar, setProfileAvatar] = useState<string | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false); // Nouvel état pour les écrans < 760px
  const [isMobileScreen, setIsMobileScreen] = useState(false); // Nouvel état pour les écrans < 760px
  const [scrolledMobile, setScrolledMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const supabase = createBrowserSupabaseClient();
  const router = useRouter();
  const { theme, systemTheme } = useTheme();
  const pathname = usePathname();
  const isBookingPage = pathname === "/booking" || pathname === "/vehicle";
  const isHomePage = pathname === '/';
  // Gérer le mode dark/light avec sécurité
  const currentTheme = mounted ? (theme === 'system' ? systemTheme : theme) : 'light';

  useEffect(() => {
    setMounted(true);
  }, []); // ✅ Pas de useEffect imbriqué

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > window.innerHeight * 0.7);
          setScrolledMobile(window.scrollY > window.innerHeight * 0.32);
          ticking = false;
        });
        ticking = true;
      }
      setIsMobile(window.innerWidth <= 1024);
      setIsSmallScreen(window.innerWidth < 760);
      setIsMobileScreen(window.innerWidth < 650);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // ✅ Un seul useEffect pour le scroll

  const shouldHaveBackground = scrolled || (isMobile && isMenuOpen || isMobileScreen && scrolledMobile) || isBookingPage

  // Définition dynamique des couleurs du texte
  const textColor = isBookingPage
    ? "text-background_sd bg-primary"
    : isMenuOpen
      ? "text-text" // Noir quand le menu est ouvert
      : currentTheme === "dark"
        ? "text-background_sd" // Blanc en dark mode
        : shouldHaveBackground
          ? "text-text bg-background " // Noir quand le fond est coloré
          : "text-background_sd bg-primary "; // Blanc quand le fond est transparent

  // useEffect(() => {
  //   async function fetchProfile() {
  //     const { data: { user } } = await supabase.auth.getUser();
  //     if (!user) {
  //       router.push("/sign-in");
  //       return;
  //     }
  //     const { data: profile } = await supabase
  //       .from("users")
  //       .select("avatar")
  //       .eq("id", user.id)
  //       .single();
  //     setProfileAvatar(profile?.avatar);
  //   }
  //   if (isAuthenticated) {
  //     fetchProfile();
  //   }
  // }, [isAuthenticated, supabase, router]);

  const handleSignOut = async () => {
    try {
      setSignOutLoading(true);
      await supabase.auth.signOut();
      await signOutAction();
      router.refresh();
    } catch (error) {
      console.error("Error during sign out:", error);
      setSignOutLoading(false);
    }
  };

  if (isAuthenticated) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-12 w-12 sm:h-8 sm:w-8 rounded-full border mb-24 sm:mb-0">
            <Avatar className="h-12 w-12 sm:h-8 sm:w-8">
              <AvatarImage
                src={profileAvatar ?? userAvatar ?? undefined}
                alt={userEmail ? userEmail.substring(0, 2).toUpperCase() : "U"}
              />
              <AvatarFallback>
                {userEmail ? userEmail.substring(0, 2).toUpperCase() : "U"}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-base font-medium leading-none text-theme-primary">Mon compte</p>
              <p className="text-sm leading-none text-theme-secondary">{userEmail}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/profile" className="cursor-pointer hover-primary">
              <UserIcon className="mr-2 h-4 w-4 icon-theme-primary" />
              <span>Profile</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={signOutLoading}>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center cursor-pointer hover-accent"
              disabled={signOutLoading}
            >
              {signOutLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="mr-2 h-4 w-4 icon-theme-primary" />
              )}
              <span>{signOutLoading ? "Déconnexion..." : "Se déconnecter"}</span>
            </button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <div className="hidden md:flex gap-2 auth_btn_ctn">
        <Button
          asChild
          size="sm"
          variant="outline"
          className={`auth_btn text-background_sd text-base bg-primary border-current transition-all duration-300 ease-in-out
                      backdrop-blur-md hover:bg-[#17252A] hover:bg-opacity-100 hover:text-background_sd hover:-translate-y-1 font-bold
                      active:scale-95`}
        >
          <Link href="/sign-in">Se connecter</Link>
        </Button>
        <Button
          asChild
          size="sm"
          variant="default"
          className={`auth_btn ${textColor} text-base bg-transparent border border-current transition-all duration-300 ease-in-out
                      hover:bg-[#17252A] hover:text-background_sd hover:-translate-y-1 font-bold
                      active:scale-95`}
        >
          <Link href="/sign-up">S'inscrire</Link>
        </Button>
      </div>

      <div className="md:hidden lucid_icon">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className={`${textColor} nav_menu_item`}>
              <LogIn className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild>
              <Link href="/sign-in" className="w-full flex items-center font-bold">
                Se connecter
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/sign-up" className="w-full flex items-center font-bold">
                S'inscrire
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
